import { inject, Injectable } from '@angular/core';

import { Image, ImageType, Product } from '@spartacus/core';

import { HeadingParagraphMapper, PushCardMapper } from '@seb/web-common/core/mappers';
import { StockStatusEnum, UppercaseThemesEnum } from '@seb/web-common/core/enums';
import { IFootNote, IPushCardWithLayout } from '@seb/web-design-system/core';
import { IArguments } from '@seb/web-design-system/arguments';
import { ImageService } from '@seb/web-common/core/services';

import {
  Bundle, ExtendedIMedia, FreeEditorial, PdpConfig, ProductBenefit, PushCardImageRatioEnum, PushCardMediaConfigEnum,
  PushCardSizeEnum, PushCardTextConfigEnum
} from '@seb/web-common/core/models';

@Injectable()
export class ProductDetailsPageService {
  private readonly headingParagraphMapper = inject(HeadingParagraphMapper);
  private readonly pushCardMapper = inject(PushCardMapper);
  private readonly imageService = inject(ImageService);

  productBenefitToIPushCardWithLayout(productBenefit: ProductBenefit): IPushCardWithLayout {
    return this.pushCardMapper.toPushCardWithLayout({
      headline: productBenefit.title,
      description: productBenefit.description,
      media: productBenefit.image,
      imageRatio: PushCardImageRatioEnum.R4_3,
      mediaConfig: PushCardMediaConfigEnum.TOP,
      textConfig: PushCardTextConfigEnum.CENTER,
      theme: UppercaseThemesEnum.LIGHT,
      pushSize: PushCardSizeEnum.XS
    });
  }

  toBundleArgumentsProductInformation({ galleryImages, claim, description }: Bundle, lameConfig?: PdpConfig): IArguments {
    const medias = galleryImages?.[ImageType.GALLERY]?.[0];

    return {
      secondaryMedia: medias?.['extra'] || medias?.['main'],
      content: {
        heading: this.headingParagraphMapper.toHeadingParagraph({
          heading: {
            headline: claim,
            description
          },
          isUnderlined: !!lameConfig?.productInfoArguments?.titleUnderlined,
          titleTheme: lameConfig?.productInfoArguments?.titleTheme
        })
      }
    };
  }

  toArgumentsProductInformation(product: Product): IArguments {
    let config: {
      heading: { description: string; headline?: string };
      images?: Image[];
    };

    if (product.star?.title && product.star?.detail && product.star?.video) {
      config = {
        images: product.star.video,
        heading: {
          description: product.star?.detail,
          headline: product.star?.title
        }
      };
    } else {
      config = {
        images: product.standardImage,
        heading: (product.updateEditorial === 'AUTOMATIC') ? {
          description: product.ssEditorialDescriptionDetail,
          headline: product.ssEditorialDescriptionTitle
        } : {
          description: product.description
        }
      };
    }

    return {
      secondaryMedia: this.getEditorialMedia((config.images)),
      content: {
        heading: this.headingParagraphMapper.toHeadingParagraph(config.heading)
      }
    };
  }

  toArgumentsFreeEditorial(freeEditorial?: FreeEditorial, mainTitle?: string, lameConfig?: PdpConfig): IArguments {
    const { image, headLine, title, detail } = freeEditorial ?? {};

    return {
      secondaryMedia: this.getEditorialMedia(image),
      content: {
        heading: {
          isUnderlined: !!lameConfig?.freeEditoBUArguments?.titleUnderlined,
          titleThemesConfig: lameConfig?.freeEditoBUArguments?.titleTheme,
          data: {
            title: headLine,
            subtitle: !!mainTitle && !!title ? title : undefined,
            description: detail
          }
        }
      }
    };
  }

  toArgumentsFreeEditorial2(freeEditorial?: FreeEditorial, mainTitle?: string, lameConfig?: PdpConfig): IArguments {
    const { secondImage, secondTitle, secondDetail } = freeEditorial ?? {};

    return {
      secondaryMedia: this.getEditorialMedia(secondImage),
      content: {
        heading: {
          isUnderlined: !!lameConfig?.freeEditoBUArguments?.titleUnderlined,
          titleThemesConfig: lameConfig?.freeEditoBUArguments?.titleTheme,
          data: {
            subtitle: (!!mainTitle && !!secondTitle) ? secondTitle : undefined,
            description: secondDetail
          }
        }
      }
    };
  }

  toFootNoteDisclaimer({ uspDisclaimer, keyClaimDisclaimer, cmmfOtherDisclaimers }: Product): IFootNote[] {
    const content: IFootNote[] = [];

    if (uspDisclaimer) {
      content.push({ note: uspDisclaimer });
    }
    if (keyClaimDisclaimer) {
      content.push({ note: keyClaimDisclaimer });
    }
    if (cmmfOtherDisclaimers) {
      content.push({ note: cmmfOtherDisclaimers });
    }

    return content;
  }

  toSnippets(product: Product, brandSite: string) {
    const brandName = (product?.brand || brandSite);
    const snippets = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      brand: {
        '@type': 'Brand',
        name: brandName
      },
      name: product.name,
      image:
        (!!product.images && product.images[ImageType.PRIMARY]?.['thumbnail']?.['url']) ||
        (!!product.images && product.images[ImageType.PRIMARY]?.['main']?.['url']) ||
        '',
      sku: product.code,
      mpn: product.commercialCode,
      offers: {
        '@type': 'Offer',
        priceCurrency: product.price?.currencyIso,
        price: product.discountedPrice ? product.discountedPrice?.value : product.price?.value,
        availability: product.stock?.stockLevelStatus === StockStatusEnum.OUT_OF_STOCK
          ? 'https://schema.org/OutOfStock'
          : 'https://schema.org/InStock'
      }
    };
    return snippets;
  }

  private getEditorialMedia(images?: Image[]): ExtendedIMedia {
    return this.imageService.normalizeMedia(
      images?.find(({ format }) => format === 'extra') || images?.find(({ format }) => format === 'main')
    );
  }
}
